import { isPlayableSampleRate } from "@/features/voice-agent/audio-constraints";
import { asRecord, readString } from "@/lib/voice/payload";

/** Short-lived agent socket credentials minted by the `elevenlabs-agent-session` edge function. */
export interface AgentSessionCredentials {
  /** Required owned reference; incomplete or pre-capability responses are refused. */
  conversationId: string;
  /** Enabled business operations from the registry; empty when none are declared. */
  operations: string[];
  outputSampleRate: number;
  signedUrl: string;
  /** The server clock at mint, in epoch milliseconds; absent from an older backend. */
  serverTime?: number;
  /** The operator's thread this voice conversation belongs to. */
  threadId: string;
  tools: string[];
}

const BUSINESS_TOOLS = new Set([
  "prepareBusinessOperation",
  "executeBusinessOperation",
  "declineBusinessOperation",
]);

interface SocketCredentials {
  outputSampleRate: number;
  signedUrl: string;
}

function parseSocketCredentials(source: Record<string, unknown>): SocketCredentials | null {
  const signedUrl = readString(source, "signedUrl");
  if (!signedUrl.startsWith("wss://")) {
    return null;
  }

  const outputSampleRate = source.outputSampleRate;
  return isPlayableSampleRate(outputSampleRate) ? { outputSampleRate, signedUrl } : null;
}

function normalizeName(value: unknown): string | null {
  if (typeof value !== "string") {
    return null;
  }
  const name = value.trim();
  return name || null;
}

function parseNames(values: unknown): string[] | null {
  if (!Array.isArray(values)) {
    return null;
  }
  const names: string[] = [];
  for (const value of values) {
    const name = normalizeName(value);
    if (!name) {
      return null;
    }
    names.push(name);
  }
  return new Set(names).size === names.length ? names : null;
}

function parseCompleteCredentials(
  source: Record<string, unknown>,
  socket: SocketCredentials,
): AgentSessionCredentials | null {
  const conversationId = readString(source, "conversationId");
  const threadId = readString(source, "threadId");
  const tools = parseNames(source.tools);
  if (!conversationId || !threadId || !tools || tools.length === 0) {
    return null;
  }
  // Fail closed on business operations only: a missing or malformed list
  // declares none, so business tools are off while read and document tools
  // keep the session usable.
  const operations = parseNames(source.operations) ?? [];
  const serverTime = Date.parse(readString(source, "serverTime"));
  return {
    ...socket,
    conversationId,
    operations,
    ...(Number.isFinite(serverTime) ? { serverTime } : {}),
    threadId,
    tools: operations.length ? tools : tools.filter((tool) => !BUSINESS_TOOLS.has(tool)),
  };
}

/**
 * Normalizes the edge-function payload. A URL that is not a secure socket and a
 * sample rate the browser cannot honour are both rejected outright rather than
 * downgraded or defaulted, so a malformed mint surfaces as a session failure
 * instead of silently mis-tuned playback.
 */
export function parseAgentSessionCredentials(payload: unknown): AgentSessionCredentials | null {
  const source = asRecord(payload);
  if (!source) {
    return null;
  }

  // The edge function owns the default and always sends a rate, so an absent or
  // unusable value means the payload is wrong — not that a fallback is needed.
  // Guessing here would decode PCM at a rate the agent is not producing.
  const socket = parseSocketCredentials(source);
  if (!socket) {
    return null;
  }

  return source.mode === "live" ? parseCompleteCredentials(source, socket) : null;
}
